import { Tornadoes } from "@common/definitions/storm";
import { GlProgram, Mesh, MeshGeometry, Shader, type Texture } from "pixi.js";
import { PIXI_SCALE } from "../utils/constants";
import { createNoiseTexture } from "../war/warNoise";

/*
    A tornado's funnel, drawn by the graphics card (0.98.5; the owner of 0.98.4's pictures: "the torrnado looks ugly
    and unatural"): one quad over the funnel, its shader drawing the cloud seen from above as a storm is from a
    satellite: arms of cloud winding in to the eye in a spiral, turning faster the nearer the middle, broken and
    billowed by noise, their leading edges lit, the thick of it dark slate and the eye near black; round its foot a
    skirt of tan dust streaked outward and turning slower; its shadow on the ground under all of it; the whole thing
    bulging and wandering a little as it turns; lightning lighting the cloud from inside now and then (the storm view's
    `flash`). The debris whirling round it, its roar and the ground it tears up are the storm view's (sprites and
    particles over and under this). The storm view keeps its sprite funnel where the shaders aren't drawn.
*/

/** how far the quad reaches from the funnel's middle, in funnel radii (the cloud and the skirt fade out before it) */
export const FUNNEL_REACH = 3.6;

export const FUNNEL_VERTEX = /* glsl */`
in vec2 aPosition;
in vec2 aUV;
out vec2 vLocal;
out vec4 vColor;

uniform mat3 uProjectionMatrix;
uniform mat3 uWorldTransformMatrix;
uniform mat3 uTransformMatrix;
uniform vec4 uColor;

void main(void) {
    mat3 mvp = uProjectionMatrix * uWorldTransformMatrix * uTransformMatrix;
    gl_Position = vec4((mvp * vec3(aPosition, 1.0)).xy, 0.0, 1.0);
    vLocal = aUV;
    // (the container's own colour and fade, as Pixi gives every mesh)
    vColor = uColor;
}
`;

export const FUNNEL_FRAGMENT = /* glsl */`precision highp float;
in vec2 vLocal;
in vec4 vColor;
out vec4 finalColor;

uniform sampler2D uNoise;
uniform float uTime;
uniform float uRadius;
uniform float uPresence;
uniform float uFlash;

const float TAU = 6.2831853;

void main(void) {
    vec2 p = vLocal / uRadius;
    float t = uTime;
    // the funnel's middle wanders as it turns
    p -= vec2(sin(t * 1.9) * 0.1, cos(t * 1.4) * 0.1);
    float r0 = length(p);
    if (r0 > ${FUNNEL_REACH.toFixed(1)}) {
        finalColor = vec4(0.0);
        return;
    }
    float a = atan(p.y, p.x);
    // its shape: no circle, bulging round as it goes
    vec4 bulge = texture(uNoise, vec2(a / TAU * 2.0 + t * 0.05, t * 0.03));
    float r = r0 * (0.86 + 0.28 * bulge.r);

    // the cloud: two broad arms of it winding in to the eye (a spiral, turning as one, its middle hurrying ahead and
    // dropping back: a turn that never winds the arms up into rings), lumpy with billows, thinning and fraying outward
    float spin = t * 2.6 + sin(t * 0.7) * 0.9 / (0.5 + r);
    float s = a + spin + log(r + 0.08) * 3.6;
    vec4 warp = texture(uNoise, vec2(r * 0.5 - t * 0.04, (a + spin * 0.6) / TAU * 2.0));
    float arms = 0.5 + 0.5 * sin(2.0 * s + (warp.r - 0.5) * 2.2);
    float lump = texture(uNoise, vec2(r * 0.9 + t * 0.02, (a + spin * 0.8) / TAU * 3.0 + 0.4)).r;
    float billow = texture(uNoise, vec2(r * 2.2 - t * 0.05, (a + spin * 0.9) / TAU * 7.0 + 0.1)).g;
    float fray = smoothstep(0.35, 2.2, r);
    float thr = 0.32 + 0.42 * fray;
    float density = smoothstep(thr - 0.24, thr + 0.2, arms * (0.6 + 0.6 * lump) + (billow - 0.5) * 0.45);
    float disc = 1.0 - smoothstep(1.6, 2.3, r);
    density *= disc;
    // the eye: the funnel's own dark throat, and the dense dark wall round it
    float wall = (1.0 - smoothstep(0.3, 0.62, r));
    float throat = 1.0 - smoothstep(0.0, 0.32, r);
    density = max(density, wall);

    // lit from above: each arm's outer edge bright, its inner edge in shadow
    float side = sin(2.0 * s + 0.7 + (warp.r - 0.5) * 2.2);
    vec3 lit = vec3(0.72, 0.73, 0.77);
    vec3 shade = vec3(0.36, 0.37, 0.42);
    vec3 col = mix(shade, lit, 0.5 + 0.5 * side);
    col *= (0.88 + 0.22 * lump) * (0.86 + 0.28 * billow);
    col = mix(col, vec3(0.27, 0.28, 0.32), wall * 0.85);
    col = mix(col, vec3(0.05, 0.05, 0.07), throat * 0.95);
    col += uFlash * vec3(0.7, 0.8, 1.0) * density * (1.0 - throat);
    float cloudA = density * 0.96;

    // under it: the skirt of dust round its foot, tan, streaked outward and turning slower; and its shadow on the ground
    float sk = a + t * 1.4 - r * 1.2;
    float streaks = texture(uNoise, vec2(r * 0.45 - t * 0.12, sk / TAU * 4.0)).r;
    float skirt = (1.0 - smoothstep(1.0, 1.9, r)) * smoothstep(0.35, 0.7, r) * smoothstep(0.3, 0.8, streaks) * 0.55;
    vec3 dust = vec3(0.58, 0.5, 0.38) * (0.85 + 0.3 * streaks);
    float shadow = (1.0 - smoothstep(0.4, 1.5, r)) * 0.42;
    float underA = skirt + shadow * (1.0 - skirt);
    vec3 underC = dust * skirt + vec3(0.03, 0.03, 0.04) * shadow * (1.0 - skirt);

    vec3 c = col * cloudA + underC * (1.0 - cloudA);
    float alpha = cloudA + underA * (1.0 - cloudA);
    finalColor = vec4(c, alpha) * uPresence * vColor;
}
`;

let noise: Texture | undefined;
const noiseTexture = (): Texture => noise ??= createNoiseTexture(false);

/** a funnel on the graphics card: a quad over it (in its tornado's container, at its middle) and its shader */
export class TornadoFunnel {
    readonly mesh: Mesh<MeshGeometry, Shader>;
    private readonly _geometry: MeshGeometry;
    private readonly _uniforms: Record<string, unknown>;

    constructor() {
        const reach = Tornadoes.radius * FUNNEL_REACH;
        const px = reach * PIXI_SCALE;
        this._geometry = new MeshGeometry({
            positions: new Float32Array([-px, -px, px, -px, px, px, -px, px]),
            uvs: new Float32Array([-reach, -reach, reach, -reach, reach, reach, -reach, reach]),
            indices: new Uint32Array([0, 1, 2, 0, 2, 3])
        });
        this._geometry.batchMode = "no-batch";
        const shader = new Shader({
            glProgram: GlProgram.from({ vertex: FUNNEL_VERTEX, fragment: FUNNEL_FRAGMENT, name: "tornado-funnel" }),
            resources: {
                uNoise: noiseTexture().source,
                funnelUniforms: {
                    uTime: { value: 0, type: "f32" },
                    uRadius: { value: Tornadoes.radius, type: "f32" },
                    uPresence: { value: 0, type: "f32" },
                    uFlash: { value: 0, type: "f32" }
                }
            }
        });
        this._uniforms = shader.resources.funnelUniforms.uniforms as Record<string, unknown>;
        this.mesh = new Mesh({ geometry: this._geometry, shader });
        this.mesh.eventMode = "none";
    }

    /** this frame: the time (s), how far formed it is (0 to 1), and the lightning in it (0 to 1) */
    update(time: number, presence: number, flash: number): void {
        const u = this._uniforms;
        u.uTime = time;
        u.uPresence = presence;
        u.uFlash = flash;
    }

    destroy(): void {
        const shader = this.mesh.shader;
        this.mesh.destroy();
        shader?.destroy();
        this._geometry.destroy(true);
    }
}
